import React from "react";
import { Play, Pause, Tv, AlertTriangle } from "../../../../components/icons";
import { Match } from "../../types";
import { canResumeEffective } from "../../lib/clockStateMachine";

interface MatchTimerDisplayProps {
  match: Match | null;
  operatorPeriod: number;
  globalClock: string;
  /**
   * @deprecated PMA-COCK-118 — the "Tiempo Efectivo"/"Tiempo Inefectivo"
   * cards these fed (`effective-clock-value`/`ineffective-clock-value`)
   * were removed from this component's render at the client's explicit
   * request, collapsing the sub-clock grid down to VAR + Time Off only.
   * The values themselves are untouched in `useMatchTimer.ts` — still
   * computed, still passed down from `LoggerView.tsx`, still rendered in
   * `AnalyticsView.tsx` (out of scope for this change) — and exposed for
   * E2E assertions via the harness's `getEffectiveIneffectiveClocks()`
   * (`useCockpitHarness.ts`), same shape as PMA-COCK-115's `getBallState`.
   */
  effectiveClock: string;
  /** @deprecated PMA-COCK-118 — see `effectiveClock` above. */
  ineffectiveClock: string;
  varClock: string;
  timeoutClock: string;
  clockMode: "EFFECTIVE" | "INEFFECTIVE";
  isClockRunning: boolean;
  onGlobalStart: () => void;
  onGlobalStop: () => void;
  locked?: boolean;
  lockReason?: string;
  onModeSwitch: (mode: "EFFECTIVE" | "INEFFECTIVE") => void;
  onVarToggle: () => void;
  onTimeoutToggle: () => void;
  isVarActive: boolean;
  isTimeoutActive: boolean;
  hideResumeButton?: boolean;
  /** Broadcast-cap main clock, e.g. "56:00" — caps at each period's regulation end. */
  broadcastMainClock: string;
  /** Broadcast-cap added time, e.g. "+02:05", or "" when there is none. */
  broadcastAddedClock: string;
  t: any;
  /** PMA-COCK-106 (3rd revision): true when rendered inside the landscape-
   * tablet rail (a ~200px column). The component's normal 3-column layout
   * switches on Tailwind's `sm:` breakpoint — a VIEWPORT media query
   * (>=640px) — from stacked to row. At a 1024px viewport that breakpoint
   * fires regardless of the rail's actual ~200px width, forcing three
   * columns (left ~192px + middle ~280px + right flex-1) into a container
   * that cannot hold them, producing unwanted horizontal overflow instead
   * of the intended single-column stack. `isRailLayout` forces the stack
   * unconditionally, independent of viewport width — the same
   * container-vs-viewport lesson already recorded on `ScoreBoard.tsx` and
   * `TacticalField.tsx`. */
  isRailLayout?: boolean;
  /** EPIC-111 UIJ-R2-1: portrait tablet (< 1024px wide, no rail). The panel
   * becomes a two-row strip: clocks inline on top, all five controls
   * (Start / Stop / Ineffective / VAR / Time-off) in one 44px row below.
   * Nothing is hidden or removed; only the arrangement changes. */
  isCompactStrip?: boolean;
}

// PMA-COCK-213: which break/gap match statuses make "Iniciar" the wrong
// action, and what the visible reason says instead — see this file's own
// `startWrongPhaseReason` comment for the full mechanism. HALFTIME and
// EXTRA_HALFTIME each have one unambiguous next ladder action, named
// directly (mirrors the ladder's own row labels, not invented copy).
// FULLTIME is a genuine 3-way fork (extra time / penalties / finish) — no
// single row is "the" correct one, so its message points at the panel
// generically rather than naming one option as if it were the only one.
// COMPLETED has no correct action at all. Every other status (an actual
// in-progress period) is intentionally absent — "Iniciar" is correct there
// and stays fully enabled, unchanged.
const START_WRONG_PHASE_REASON: Partial<
  Record<string, { labelKey: string; labelFallback: string }>
> = {
  Halftime: {
    labelKey: "startWrongPhaseHalftime",
    labelFallback: 'Usa "Iniciar 2do tiempo" en el panel de fases.',
  },
  Extra_Halftime: {
    labelKey: "startWrongPhaseExtraHalftime",
    labelFallback: 'Usa "Iniciar T. extra 2" en el panel de fases.',
  },
  Fulltime: {
    labelKey: "startWrongPhaseFulltime",
    labelFallback: "Usa el panel de fases para continuar el partido.",
  },
  Completed: {
    labelKey: "startWrongPhaseCompleted",
    labelFallback: "El partido ya terminó.",
  },
};

const MatchTimerDisplay: React.FC<MatchTimerDisplayProps> = ({
  match,
  operatorPeriod,
  globalClock,
  varClock,
  timeoutClock,
  clockMode,
  isClockRunning,
  onGlobalStart,
  onGlobalStop,
  locked = false,
  lockReason,
  onModeSwitch,
  onVarToggle,
  onTimeoutToggle,
  isVarActive,
  isTimeoutActive,
  hideResumeButton = false,
  broadcastMainClock,
  broadcastAddedClock,
  t,
  isRailLayout = false,
  isCompactStrip = false,
}) => {
  const clockLocked =
    locked || match?.status === "Fulltime" || match?.status === "Completed";
  // PMA-COCK-213 (EPIC-61, lead product decision, client wave 2026-09-19).
  // "Iniciar" stays wired to its correct within-period job — (re)starting
  // the clock while genuinely mid-period (FIRST_HALF/SECOND_HALF/extra
  // time/PENALTIES) — it is NOT retired. The diagnosed defect (PMA-COCK-210
  // AC1) was that `startDisabled` above never checked match STATUS at all,
  // only `current_period_start_timestamp` — so at a break/gap status this
  // button stayed enabled and, on tap, called `updateMatchStatus(id,
  // undefined, "start")`: no `status` sent, so the backend's own
  // `clock_operation === "start"` branch (`matches_new.py:1114`) stamps
  // `current_period_start_timestamp` for the CURRENT unchanged status/period
  // only — a real clock starts ticking against the STALE prior period's
  // baseline while `match.status` stays frozen, with nothing on screen
  // reflecting it (see `team_findings.md`'s own stale-baseline-clock finding
  // for the full mechanism and its comparison to the prior 237-second
  // dropped-clock-mode-PATCH production incident).
  //
  // Fixed here at the ONE button that can reach that bare clock-only path —
  // gated on `match.status` at each break/gap status where a DIFFERENT,
  // named ladder control (`MatchPeriodSelector.tsx`, now in the rail right
  // below this component, PMA-COCK-210) is the actually-correct action.
  // `!locked` avoids double-messaging when the pre-existing
  // `clock-locked-banner` above already explains an admin lock/finished
  // match for the same tap.
  const startWrongPhaseReason =
    !locked && match?.status
      ? START_WRONG_PHASE_REASON[match.status]
      : undefined;
  const startWrongPhaseMessage = startWrongPhaseReason
    ? t(startWrongPhaseReason.labelKey, startWrongPhaseReason.labelFallback)
    : undefined;
  // Folds the new wrong-phase gate in alongside the two pre-existing
  // reasons this button can already be disabled for (admin/finished-match
  // lock via `clockLocked`, already-started via the timestamp check) — a
  // break/gap status now disables the button even when
  // `current_period_start_timestamp` happens to be falsy, closing the exact
  // gap this UC exists to close.
  const startDisabled =
    clockLocked ||
    !!match?.current_period_start_timestamp ||
    !!startWrongPhaseReason;
  const stopDisabled =
    clockLocked ||
    (!match?.current_period_start_timestamp &&
      !(
        match?.period_timestamps?.[String(operatorPeriod)]?.start &&
        !match?.period_timestamps?.[String(operatorPeriod)]?.end
      ));
  const modeSwitchDisabled =
    clockLocked || !match?.current_period_start_timestamp;
  // PMA-COCK-126: pressing VAR/Time Off while the ball is genuinely in play
  // double-counts match time (computeIneffectiveBreakdown treats these as
  // pausing an already-active stoppage; with none active they open a
  // parallel neutral accumulation). Enabled whenever the ball is out of
  // play: clockMode is INEFFECTIVE, or the global clock is paused.
  // Mirrored in useCockpitClockHandlers.ts as a handler-level guard
  // (defense in depth — this is the affordance, that is the rule).
  const clockRunningBlocksNeutralTimerStart =
    clockMode === "EFFECTIVE" && isClockRunning;
  // The button-level affordance must mirror the handler's "always allow
  // stop" rule (useCockpitClockHandlers.ts only gates `nextActive &&`): an
  // already-active VAR/timeout must stay clickable so the operator can turn
  // it off, even while this gate would otherwise block a START.
  //
  // PMA-COCK-154: a native `disabled` button fires NO click/pointer event at
  // all — that is exactly the "dead tap, no feedback" the operator reported
  // for TIEMPO FUERA. `locked` keeps the old fully-`disabled` DOM treatment
  // (there is no operator action worth taking once the match itself is
  // locked). The clock-running block, when NOT locked, is now
  // `aria-disabled` + styled as blocked but left DOM-clickable, so the click
  // still reaches `onVarToggle`/`onTimeoutToggle` (useCockpitClockHandlers.ts),
  // which shows a "Stop play first" toast instead of silently doing nothing.
  const varToggleHardDisabled = locked && !isVarActive;
  const timeoutToggleHardDisabled = locked && !isTimeoutActive;
  const varToggleBlockedByRunningClock =
    !locked && clockRunningBlocksNeutralTimerStart && !isVarActive;
  const timeoutToggleBlockedByRunningClock =
    !locked && clockRunningBlocksNeutralTimerStart && !isTimeoutActive;
  const varToggleDisabled = varToggleHardDisabled;
  const timeoutToggleDisabled = timeoutToggleHardDisabled;
  const varToggleAppearsBlocked =
    varToggleHardDisabled || varToggleBlockedByRunningClock;
  const timeoutToggleAppearsBlocked =
    timeoutToggleHardDisabled || timeoutToggleBlockedByRunningClock;
  const varToggleTitle = varToggleAppearsBlocked
    ? t("neutralTimerBlocked", "Stop play first")
    : undefined;
  const timeoutToggleTitle = timeoutToggleAppearsBlocked
    ? t("neutralTimerBlocked", "Stop play first")
    : undefined;
  const varToggleBlockedClass = varToggleAppearsBlocked
    ? "opacity-50 cursor-not-allowed"
    : "";
  const timeoutToggleBlockedClass = timeoutToggleAppearsBlocked
    ? "opacity-50 cursor-not-allowed"
    : "";
  // PMA-COCK-144: the global "Reanudar Tiempo Efectivo" resume control must
  // reject while a scoped stoppage (VAR/timeout) is open — the operator must
  // close it via its own "Finalizar" control (btn-var-toggle/
  // btn-timeout-toggle above) first. This is the UI affordance half of the
  // fix; the handler-level rejection (clockStateMachine.ts, wired through
  // useCockpitClockHandlers.ts's handleModeSwitchGuarded) is the actual rule
  // and fires independent of this `disabled` state.
  const resumeBlockedByScopedStoppage = !canResumeEffective({
    clockMode,
    isVarActive,
    isTimeoutActive,
  });
  const resumeDisabled = locked || resumeBlockedByScopedStoppage;
  const resumeTitle = resumeBlockedByScopedStoppage
    ? t("resumeBlockedScopedStoppage", "Finish the active VAR or timeout first")
    : undefined;
  const lockNotice =
    lockReason || t("lockNotice", "Cockpit locked. Match is finished.");
  const trimMs = (value: string) => value.split(".")[0] || value;
  const globalClockDisplay = trimMs(globalClock);

  const periodClockDisplay = broadcastAddedClock ? (
    <div className="flex items-baseline gap-1 flex-wrap">
      <span>{broadcastMainClock}</span>
      <span
        data-testid="match-added-time"
        className="text-xl text-yellow-500 font-bold animate-pulse"
      >
        {broadcastAddedClock}
      </span>
    </div>
  ) : (
    broadcastMainClock
  );

  return (
    <div className="bg-slate-800 rounded-lg p-3 border border-slate-700 shadow-sm">
      {locked && (
        <div
          className="mb-2 flex items-center gap-2 text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3 shadow-sm"
          data-testid="clock-locked-banner"
        >
          <AlertTriangle size={16} className="shrink-0 text-amber-400" />
          <span>{lockNotice}</span>
        </div>
      )}

      {/*
       * Compact 3-column layout at sm+ (≥640px viewport):
       *   Left  — period clock + global clock + ball state
       *   Middle — sub-clock grid (VAR / Time Off — Effective/Ineffective
       *            cards removed under PMA-COCK-118, see `effectiveClock`
       *            doc block above)
       *   Right  — Start/Stop + mode-switch controls
       * Below sm: columns stack vertically (still more compact than original).
       */}
      <div
        className={
          isRailLayout
            ? "flex flex-col gap-2"
            : isCompactStrip
              ? "flex flex-col gap-2"
              : "flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-stretch"
        }
      >
        {/* ── Left: Clock display ── */}
        <div
          className={
            isRailLayout
              ? "flex flex-col gap-1.5"
              : isCompactStrip
                ? "flex flex-row flex-wrap items-center gap-x-3 gap-y-1"
                : "sm:w-48 sm:shrink-0 flex flex-col gap-1.5 sm:border-r sm:border-slate-700 sm:pr-3"
          }
        >
          <div className="flex items-center gap-2">
            <p className="text-xs font-semibold text-slate-400">
              {t("periodClock", "Period Clock")}
            </p>
            {isClockRunning && (
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
              </span>
            )}
          </div>
          <span
            data-testid="global-clock-value"
            className={`text-3xl font-mono font-bold tracking-wider leading-none ${
              isClockRunning ? "text-green-400" : "text-slate-100"
            }`}
          >
            {periodClockDisplay}
          </span>
          <div className="flex items-center justify-between bg-slate-900/50 border border-slate-700 rounded px-2 py-1">
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
              {t("globalClock", "Global")}
            </span>
            <span
              data-testid="global-cumulative-clock"
              className="text-xs font-mono font-bold text-slate-400"
            >
              {globalClockDisplay}
            </span>
          </div>
        </div>

        {/* ── Middle: Sub-clocks 2×2 ── */}
        <div
          className={
            isRailLayout
              ? "grid grid-cols-2 gap-2"
              : isCompactStrip
                ? "grid grid-cols-2 gap-2"
                : "flex-1 min-w-0 sm:max-w-[280px] grid grid-cols-2 gap-2"
          }
        >
          <div
            className="p-2 rounded border bg-slate-700/30 border-slate-700"
            data-testid="var-time-card"
          >
            <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">
              {t("varTime", "VAR Time")}
            </p>
            <div
              data-testid="var-clock-value"
              className="font-mono font-bold text-base text-amber-300"
            >
              {trimMs(varClock)}
            </div>
          </div>
          <div
            className="p-2 rounded border bg-slate-700/30 border-slate-700"
            data-testid="timeout-time-card"
          >
            <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">
              {t("timeOff", "Time Off")}
            </p>
            <div
              data-testid="timeout-clock-value"
              className="font-mono font-bold text-base text-sky-300"
            >
              {trimMs(timeoutClock)}
            </div>
            <div
              data-testid="timeout-state-label"
              className={`text-[10px] font-semibold mt-0.5 ${
                isTimeoutActive ? "text-sky-200" : "text-slate-400"
              }`}
            >
              {isTimeoutActive
                ? t("timeOffActive", "Active")
                : t("timeOffInactive", "Inactive")}
            </div>
          </div>
        </div>

        {/* ── Right: Controls ── */}
        <div
          className={
            isRailLayout
              ? "flex flex-col gap-2.5"
              : isCompactStrip
                ? "grid grid-cols-5 grid-rows-[44px] gap-2"
                : "sm:flex-1 flex flex-col gap-2.5 sm:border-l sm:border-slate-700 sm:pl-3"
          }
        >
          {/* Start / Stop */}
          <div
            className={isCompactStrip ? "contents" : "grid grid-cols-2 gap-2"}
          >
            <button
              data-testid="btn-start-clock"
              onClick={onGlobalStart}
              disabled={startDisabled}
              title={startWrongPhaseMessage}
              className="min-h-11 px-2 py-2.5 bg-emerald-900/40 text-emerald-300 border border-emerald-500/30 rounded hover:bg-emerald-900/60 text-xs font-bold uppercase disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1 transition-colors"
            >
              <Play size={12} />
              {t("start", "Start")}
            </button>
            <button
              data-testid="btn-stop-clock"
              onClick={onGlobalStop}
              disabled={stopDisabled}
              className="min-h-11 px-2 py-2.5 bg-amber-900/40 text-amber-300 border border-amber-500/30 rounded hover:bg-amber-900/60 text-xs font-bold uppercase disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1 transition-colors"
            >
              <Pause size={12} />
              {t("stop", "Stop")}
            </button>
          </div>

          {/* PMA-COCK-213: visible, not just a hover title — mirrors the
              ladder's own `transition-reason` pattern (never leave a
              disabled control looking merely broken). */}
          {startWrongPhaseMessage && (
            <div
              data-testid="clock-start-wrong-phase-reason"
              className={`text-[11px] leading-snug text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1 ${isCompactStrip ? "col-span-5" : ""}`}
            >
              {startWrongPhaseMessage}
            </div>
          )}

          {/* Mode controls */}
          {clockMode === "EFFECTIVE" ? (
            <div
              className={isCompactStrip ? "contents" : "flex flex-col gap-2"}
            >
              <button
                data-testid="btn-ineffective-event"
                onClick={() => onModeSwitch("INEFFECTIVE")}
                disabled={modeSwitchDisabled}
                className={`w-full min-h-11 flex items-center justify-center gap-1.5 ${isCompactStrip ? "py-0.5 px-1 leading-tight text-center tracking-normal" : "py-2.5 tracking-wider"} bg-rose-900/30 text-rose-300 border border-rose-500/30 hover:bg-rose-900/50 rounded font-bold text-xs uppercase transition-colors disabled:opacity-50`}
              >
                <Pause size={12} />
                {t("ineffectiveEvent", "Ineffective")}
              </button>
              {/* PMA-COCK-207 (EPIC-61 brief 6, client wave 2026-09-19):
                  VAR/T.Fuera gain the same `min-h-11` (44px) touch-target
                  floor the rail's other controls already carry
                  (RefereeActionBar.tsx's card/injury tiles) — this pair had
                  no explicit floor before, only `py-2.5` padding, so it
                  could render a shade smaller than its siblings. Applied to
                  all 4 instances (both clockMode branches) for consistency;
                  the button's own icon/text/colour styling is unchanged. */}
              <div
                className={
                  isCompactStrip ? "contents" : "grid grid-cols-2 gap-2"
                }
              >
                <button
                  data-testid="btn-var-toggle"
                  onClick={onVarToggle}
                  disabled={varToggleDisabled}
                  aria-disabled={varToggleAppearsBlocked || undefined}
                  title={varToggleTitle}
                  className={`flex items-center justify-center gap-1 min-h-11 py-2.5 rounded font-bold text-xs uppercase tracking-wider transition-colors border disabled:opacity-50 ${varToggleBlockedClass} ${
                    isVarActive
                      ? "bg-amber-900/40 text-amber-200 border-amber-500/40"
                      : "bg-slate-900/40 text-amber-300 border-amber-500/30 hover:bg-slate-900/60"
                  }`}
                >
                  <Tv size={11} />
                  {t("varToggle", "VAR")}
                </button>
                <button
                  data-testid="btn-timeout-toggle"
                  onClick={onTimeoutToggle}
                  disabled={timeoutToggleDisabled}
                  aria-disabled={timeoutToggleAppearsBlocked || undefined}
                  title={timeoutToggleTitle}
                  className={`flex items-center justify-center gap-1 min-h-11 py-2.5 rounded font-bold text-xs uppercase tracking-wider transition-colors border disabled:opacity-50 ${timeoutToggleBlockedClass} ${
                    isTimeoutActive
                      ? "bg-sky-900/40 text-sky-200 border-sky-500/40"
                      : "bg-slate-900/40 text-sky-300 border-sky-500/30 hover:bg-slate-900/60"
                  }`}
                >
                  <Pause size={11} />
                  {isTimeoutActive
                    ? t("timeOffStop", "End T.Off")
                    : t("timeOffStart", "T.Off")}
                </button>
              </div>
            </div>
          ) : (
            <div
              className={isCompactStrip ? "contents" : "grid grid-cols-2 gap-2"}
            >
              {/* PMA-COCK-144: while clockMode is non-EFFECTIVE (this
               * branch), an active VAR/timeout is a SCOPED stoppage — the
               * global resume control below is disabled for it, so this is
               * now the ONLY control that can end it. Promoted to a solid,
               * ring-highlighted "primary" treatment (vs. the translucent
               * /40 inactive style) so it visually reads as the one button
               * to press, per the client's explicit ask. */}
              <button
                data-testid="btn-var-toggle"
                onClick={onVarToggle}
                disabled={varToggleDisabled}
                aria-disabled={varToggleAppearsBlocked || undefined}
                title={varToggleTitle}
                className={`flex items-center justify-center gap-1 min-h-11 py-2.5 rounded font-bold text-xs uppercase tracking-wider transition-colors border disabled:opacity-50 ${varToggleBlockedClass} ${
                  isVarActive
                    ? "bg-amber-500 text-slate-950 border-amber-400 ring-2 ring-amber-300/60 shadow-lg shadow-amber-900/40"
                    : "bg-slate-900/40 text-amber-300 border-amber-500/30 hover:bg-slate-900/60"
                }`}
              >
                <Tv size={11} />
                {isVarActive ? t("varStop", "End VAR") : t("varToggle", "VAR")}
              </button>
              <button
                data-testid="btn-timeout-toggle"
                onClick={onTimeoutToggle}
                disabled={timeoutToggleDisabled}
                aria-disabled={timeoutToggleAppearsBlocked || undefined}
                title={timeoutToggleTitle}
                className={`flex items-center justify-center gap-1 min-h-11 py-2.5 rounded font-bold text-xs uppercase tracking-wider transition-colors border disabled:opacity-50 ${timeoutToggleBlockedClass} ${
                  isTimeoutActive
                    ? "bg-sky-500 text-slate-950 border-sky-400 ring-2 ring-sky-300/60 shadow-lg shadow-sky-900/40"
                    : "bg-slate-900/40 text-sky-300 border-sky-500/30 hover:bg-slate-900/60"
                }`}
              >
                <Pause size={11} />
                {isTimeoutActive
                  ? t("timeOffStop", "End T.Off")
                  : t("timeOffStart", "T.Off")}
              </button>
            </div>
          )}

          {clockMode !== "EFFECTIVE" &&
            (!hideResumeButton ? (
              <button
                data-testid="btn-resume-effective"
                onClick={() => onModeSwitch("EFFECTIVE")}
                disabled={resumeDisabled}
                title={resumeTitle}
                className={`w-full flex items-center justify-center gap-1.5 py-2 bg-emerald-600 text-white hover:bg-emerald-700 border border-emerald-500 rounded font-bold text-xs uppercase tracking-wider transition-colors shadow-lg shadow-emerald-900/20 disabled:opacity-50 disabled:cursor-not-allowed ${isCompactStrip ? "col-span-5" : ""}`}
              >
                <Play size={12} />
                {t("resumeEffective", "Resume Effective Time")}
              </button>
            ) : (
              // PMA-COCK-118: when the floating field-overlay resume affordance
              // is showing instead of this one (hideResumeButton), this row
              // used to just disappear — since PMA-COCK-118 shrank the sub-clock
              // grid above (Effective/Ineffective cards removed), that grid no
              // longer masks the resulting height difference between clockMode
              // states, so this control stack itself must stay a constant
              // height regardless of hideResumeButton. Invisible, not removed —
              // deliberately NOT `data-testid="btn-resume-effective"` (unlike
              // the real button above), so `logger-halftime-resume-regression
              // .spec.ts`'s "zero DOM matches for btn-resume-effective during a
              // halftime break" invariant is untouched.
              <div
                aria-hidden="true"
                className={`invisible w-full flex items-center justify-center gap-1.5 py-2 border rounded font-bold text-xs uppercase tracking-wider ${isCompactStrip ? "hidden" : ""}`}
              >
                <Play size={12} />
                {t("resumeEffective", "Resume Effective Time")}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default MatchTimerDisplay;
